import { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import { daysLeft } from "../lib/license";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { ShieldCheck, CalendarPlus, Save, Power, Landmark, Clock, Inbox, Trash2, Mail, Sparkles, Database, Gem, Users, Search, HeartPulse, Wind, BadgeCheck, ExternalLink, FileText } from "lucide-react";
import { ToothIcon } from "./Odontogram";

function BF({ label, k, value, onChange, placeholder }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">{label}</Label>
      <Input data-testid={`billing-${k}`} value={value || ""} placeholder={placeholder}
        onChange={(e) => onChange(k, e.target.value)}
        className="rounded-xl mt-1.5 bg-stone-50 border-transparent focus:bg-white" />
    </div>
  );
}

function statusBadge(d) {
  if (d.role === "admin")
    return <Badge className="bg-brand-sage text-white">Admin</Badge>;
  if (d.license_active) {
    const dl = daysLeft(d);
    if (dl !== null && dl <= 5)
      return <Badge className="bg-amber-500 text-white" data-testid={`badge-expiring-${d.user_id}`}>Vence en {dl} {dl === 1 ? "día" : "días"}</Badge>;
    return <Badge className="bg-emerald-600 text-white">Activo</Badge>;
  }
  return <Badge variant="destructive">Vencido</Badge>;
}

export default function AdminPanel() {
  const [docs, setDocs] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dates, setDates] = useState({});
  const [billing, setBilling] = useState({ bank: "", clabe: "", account_holder: "", amount: "150", concept: "", note: "" });
  const [savingBilling, setSavingBilling] = useState(false);
  const [aiUsage, setAiUsage] = useState(null);
  const [accessLog, setAccessLog] = useState([]);
  const [docSearch, setDocSearch] = useState("");
  const [notices, setNotices] = useState([]);
  const [receiptView, setReceiptView] = useState(null);
  const token = localStorage.getItem("session_token");
  const receiptUrl = (id) => `${API}/admin/transfer-notices/${id}/receipt?auth=${token}`;

  const load = async () => {
    setLoading(true);
    try {
      const [d, b] = await Promise.all([api.get("/admin/doctors"), api.get("/billing-info")]);
      setDocs(d.data || []);
      if (b.data && Object.keys(b.data).length) setBilling((s) => ({ ...s, ...b.data }));
      try { const l = await api.get("/admin/leads"); setLeads(l.data || []); } catch { /* noop */ }
      try { const u = await api.get("/admin/ai-usage"); setAiUsage(u.data); } catch { /* noop */ }
      try { const al = await api.get("/admin/access-log"); setAccessLog(al.data || []); } catch { /* noop */ }
      try { const tn = await api.get("/admin/transfer-notices"); setNotices(tn.data || []); } catch { /* noop */ }
    } catch (e) {
      toast.error("No se pudo cargar la información");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const update = async (userId, payload, msg) => {
    try {
      await api.put(`/admin/doctors/${userId}`, payload);
      toast.success(msg || "Actualizado");
      load();
    } catch (e) {
      toast.error("No se pudo actualizar");
    }
  };

  const decideVerif = async (userId, decision) => {
    let note = null;
    if (decision !== "verified") {
      note = window.prompt(decision === "rejected" ? "Motivo del rechazo (opcional):" : "¿Qué debe corregir el médico?");
      if (note === null && decision === "correction") return;
    }
    try {
      await api.post(`/admin/verifications/${userId}`, { decision, note });
      toast.success(decision === "verified" ? "Médico verificado" : decision === "rejected" ? "Verificación rechazada" : "Corrección solicitada");
      load();
    } catch { toast.error("No se pudo actualizar la verificación"); }
  };

  const saveBilling = async () => {
    setSavingBilling(true);
    try {
      await api.put("/admin/billing-info", billing);
      toast.success("Datos bancarios guardados");
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally {
      setSavingBilling(false);
    }
  };

  const expiring = docs.filter((d) => d.role !== "admin" && d.license_active && daysLeft(d) !== null && daysLeft(d) <= 5);

  const filteredDocs = docs.filter((d) => {
    const s = docSearch.trim().toLowerCase();
    if (!s) return true;
    return `${d.name || ""} ${d.email || ""}`.toLowerCase().includes(s);
  });
  const activeCount = docs.filter((d) => d.role !== "admin" && d.license_active).length;
  const pendingVerif = docs.filter((d) => d.verification_status === "pending" || d.verification_status === "correction");

  const setBF = (k, v) => setBilling((s) => ({ ...s, [k]: v }));

  const PLAN_LABEL = { base: "Básico", dental: "Odontológico", pro: "Pro" };
  const approveNotice = async (id) => {
    try {
      await api.post(`/admin/transfer-notices/${id}/approve`);
      toast.success("Pago confirmado y cuenta reactivada (1 mes)");
      load();
    } catch { toast.error("No se pudo reactivar"); }
  };
  const rejectNotice = async (id) => {
    if (!window.confirm("¿Marcar este aviso como rechazado? No se reactivará la cuenta.")) return;
    try {
      await api.post(`/admin/transfer-notices/${id}/reject`);
      toast.success("Aviso rechazado");
      load();
    } catch { toast.error("No se pudo actualizar"); }
  };
  const pendingNotices = notices.filter((n) => n.status === "pendiente");

  const deleteLead = async (id) => {
    try { await api.delete(`/admin/leads/${id}`); setLeads((s) => s.filter((l) => l.lead_id !== id)); toast.success("Prospecto eliminado"); }
    catch { toast.error("No se pudo eliminar"); }
  };

  return (
    <div className="space-y-6 clinped-enter" data-testid="admin-panel">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-brand-terracotta/10 flex items-center justify-center">
          <ShieldCheck className="text-brand-terracotta" size={24} />
        </div>
        <div>
          <h1 className="font-outfit font-bold text-2xl text-brand-ink">Administración de licencias</h1>
          <p className="text-sm text-muted-foreground">Gestiona el acceso de los médicos. Pago: ${billing.amount || "150"} MXN/mes.</p>
        </div>
      </div>

      {/* Aviso de por vencer */}
      {expiring.length > 0 && (
        <div data-testid="expiring-alert" className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
          <Clock className="text-amber-600 shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-amber-900">
            <p className="font-semibold mb-1">{expiring.length} licencia(s) por vencer (≤ 5 días)</p>
            <p>{expiring.map((d) => `${d.name} (${daysLeft(d)}d)`).join(" · ")}</p>
          </div>
        </div>
      )}

      {/* Pagos por transferencia (avisos de médicos) */}
      <div className="bg-white rounded-2xl border border-stone-100 p-5 shadow-sm" data-testid="transfer-notices-panel">
        <div className="flex items-center gap-2 mb-3">
          <Landmark className="text-brand-sage" size={20} />
          <h2 className="font-outfit font-semibold text-brand-ink">Pagos por transferencia</h2>
          <span data-testid="transfer-pending-count"
            className={`ml-1 text-xs font-bold px-2.5 py-1 rounded-full ${pendingNotices.length ? "bg-amber-500 text-white" : "bg-stone-100 text-stone-500"}`}>
            {pendingNotices.length} por revisar
          </span>
        </div>
        <p className="text-xs text-muted-foreground mb-4">Médicos que avisaron que ya pagaron por transferencia. Verifica el comprobante en tu correo y reactiva la cuenta con un toque.</p>
        {pendingNotices.length === 0 ? (
          <p className="text-sm text-muted-foreground">No hay pagos por transferencia pendientes de revisar.</p>
        ) : (
          <div className="space-y-3">
            {pendingNotices.map((n) => (
              <div key={n.notice_id} data-testid={`transfer-notice-${n.notice_id}`} className="rounded-xl border border-amber-200 bg-amber-50/50 p-3.5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-outfit font-semibold text-brand-ink">{n.name || "—"}</p>
                    <p className="text-xs text-brand-ink/60 break-all">{n.email}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Badge className="bg-brand-terracotta text-white">Plan {PLAN_LABEL[n.plan] || n.plan}</Badge>
                      <span className="text-sm font-bold text-brand-ink">${n.amount} MXN</span>
                      <span className="text-xs text-muted-foreground">{n.created_at ? new Date(n.created_at).toLocaleString("es-MX") : ""}</span>
                    </div>
                    {n.has_receipt ? (
                      n.receipt_is_image !== false ? (
                        <button data-testid={`transfer-receipt-${n.notice_id}`} onClick={() => setReceiptView(receiptUrl(n.notice_id))}
                          className="mt-2 inline-flex items-center gap-2 group">
                          <img src={receiptUrl(n.notice_id)} alt="Comprobante" className="w-16 h-16 rounded-lg object-cover border border-stone-200 group-hover:ring-2 ring-brand-sage transition-all" />
                          <span className="text-xs font-semibold text-brand-sageDark underline">Ver comprobante</span>
                        </button>
                      ) : (
                        <a href={receiptUrl(n.notice_id)} target="_blank" rel="noreferrer" data-testid={`transfer-receipt-${n.notice_id}`}
                          className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-sageDark underline"><FileText size={13} /> Ver comprobante (PDF)</a>
                      )
                    ) : (
                      <p className="mt-2 text-xs text-amber-600 font-semibold">Sin comprobante adjunto</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" data-testid={`transfer-approve-${n.notice_id}`} onClick={() => approveNotice(n.notice_id)}
                      className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white">
                      <Power size={14} /> Confirmar pago y reactivar
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`transfer-reject-${n.notice_id}`} onClick={() => rejectNotice(n.notice_id)}
                      className="gap-1.5 text-red-600 hover:bg-red-50">Rechazar</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cola de verificaciones */}
      <div className="bg-white rounded-2xl border border-stone-100 p-5 shadow-sm" data-testid="verification-queue">
        <div className="flex items-center gap-2 mb-3">
          <BadgeCheck className="text-teal-600" size={20} />
          <h2 className="font-outfit font-semibold text-brand-ink">Verificaciones</h2>
          <span data-testid="verif-pending-count"
            className={`ml-1 text-xs font-bold px-2.5 py-1 rounded-full ${pendingVerif.length ? "bg-amber-500 text-white" : "bg-stone-100 text-stone-500"}`}>
            {pendingVerif.length} pendiente{pendingVerif.length === 1 ? "" : "s"}
          </span>
        </div>
        {pendingVerif.length === 0 ? (
          <p className="text-sm text-muted-foreground">No hay solicitudes de verificación pendientes. 🎉</p>
        ) : (
          <div className="space-y-3">
            {pendingVerif.map((d) => (
              <div key={d.user_id} data-testid={`verif-queue-${d.user_id}`} className="rounded-xl border border-stone-100 bg-stone-50/60 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm">
                    <p className="font-semibold text-brand-ink">{d.name || "—"} {d.verification_status === "correction" && <span className="text-[11px] text-amber-600 font-bold">(corrección enviada)</span>}</p>
                    <p className="text-xs text-brand-ink/60">
                      Cédula: <b>{d.verification_cedula || d.cedula || "—"}</b>
                      {d.verification_profession ? <> · {d.verification_profession}</> : null}
                      {d.verification_specialty_cedula ? <> · Esp: <b>{d.verification_specialty_cedula}</b></> : null}
                      {d.email ? <> · {d.email}</> : null}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <a href="https://www.cedulaprofesional.sep.gob.mx/cedula/presidencia/indexAvanzada.action" target="_blank" rel="noreferrer"
                      data-testid={`verif-queue-sep-${d.user_id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 px-3 py-1.5 rounded-lg transition-colors">
                      <ExternalLink size={13} /> Consultar SEP
                    </a>
                    <Button size="sm" data-testid={`verif-queue-approve-${d.user_id}`} onClick={() => decideVerif(d.user_id, "verified")} className="gap-1.5 bg-teal-600 hover:bg-teal-700 text-white"><BadgeCheck size={14} /> Verificar</Button>
                    <Button size="sm" variant="outline" data-testid={`verif-queue-correction-${d.user_id}`} onClick={() => decideVerif(d.user_id, "correction")} className="gap-1.5">Corrección</Button>
                    <Button size="sm" variant="outline" data-testid={`verif-queue-reject-${d.user_id}`} onClick={() => decideVerif(d.user_id, "rejected")} className="gap-1.5 text-red-600 hover:bg-red-50">Rechazar</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Ahorro de IA */}
      {aiUsage && (
        <div className="bg-white rounded-2xl border border-stone-100 p-5 shadow-sm" data-testid="ai-usage-panel">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="text-brand-terracotta" size={20} />
            <h2 className="font-outfit font-semibold text-brand-ink">Ahorro de IA — {aiUsage.current?.month}</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="bg-stone-50 rounded-2xl p-4">
              <p className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/50 flex items-center gap-1"><Sparkles size={12} /> Generadas (IA)</p>
              <p className="text-2xl font-outfit font-bold text-brand-ink mt-1" data-testid="ai-generated">{aiUsage.current?.generated || 0}</p>
              <p className="text-[11px] text-muted-foreground">consumen tokens</p>
            </div>
            <div className="bg-emerald-50 rounded-2xl p-4">
              <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-700/70 flex items-center gap-1"><Database size={12} /> Servidas de caché</p>
              <p className="text-2xl font-outfit font-bold text-emerald-700 mt-1" data-testid="ai-cached">{aiUsage.current?.cached || 0}</p>
              <p className="text-[11px] text-emerald-700/70">gratis, sin tokens</p>
            </div>
            <div className="bg-brand-terracotta/10 rounded-2xl p-4 col-span-2 sm:col-span-1">
              <p className="text-[11px] font-bold uppercase tracking-wide text-brand-terracotta/80">Ahorro este mes</p>
              <p className="text-2xl font-outfit font-bold text-brand-terracotta mt-1" data-testid="ai-savings-pct">{aiUsage.savings_pct || 0}%</p>
              <p className="text-[11px] text-brand-terracotta/70">de llamadas evitadas</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Las respuestas deterministas (hitos, vacunas, consentimientos, recomendaciones, CIE-10 y fármacos por peso) se guardan en caché global y se reutilizan sin gastar IA.</p>
        </div>
      )}

      {/* Datos bancarios */}
      <div className="bg-white rounded-2xl border border-stone-100 p-5 shadow-sm" data-testid="billing-editor">
        <div className="flex items-center gap-2 mb-4">
          <Landmark className="text-brand-sage" size={20} />
          <h2 className="font-outfit font-semibold text-brand-ink">Datos para transferencia (SPEI)</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4">Estos datos se muestran a los médicos en la pantalla de pago cuando su licencia vence.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <BF label="Titular de la cuenta" k="account_holder" value={billing.account_holder} onChange={setBF} placeholder="Néstor Gibran López Díaz" />
          <BF label="Banco" k="bank" value={billing.bank} onChange={setBF} placeholder="BBVA" />
          <BF label="CLABE (18 dígitos)" k="clabe" value={billing.clabe} onChange={setBF} placeholder="012345678901234567" />
          <BF label="Monto mensual (MXN)" k="amount" value={billing.amount} onChange={setBF} placeholder="150" />
          <BF label="Concepto sugerido" k="concept" value={billing.concept} onChange={setBF} placeholder="Suscripción ClinPed" />
          <BF label="Nota (opcional)" k="note" value={billing.note} onChange={setBF} placeholder="Envía tu comprobante por correo" />
        </div>
        <Button onClick={saveBilling} disabled={savingBilling} data-testid="save-billing"
          className="mt-4 bg-brand-terracotta hover:bg-brand-terracottaDark text-white gap-2">
          <Save size={16} /> {savingBilling ? "Guardando…" : "Guardar datos bancarios"}
        </Button>
      </div>

      {/* Prospectos (leads de la landing) */}
      <div className="bg-white rounded-2xl border border-stone-100 p-5 shadow-sm" data-testid="leads-panel">
        <div className="flex items-center gap-2 mb-1">
          <Inbox className="text-brand-terracotta" size={20} />
          <h2 className="font-outfit font-semibold text-brand-ink">Prospectos del sitio web</h2>
          <Badge className="bg-brand-ink text-white ml-1" data-testid="leads-count">{leads.length}</Badge>
        </div>
        <p className="text-xs text-muted-foreground mb-4">Mensajes recibidos desde el formulario de contacto de la portada.</p>
        {leads.length === 0 ? (
          <p className="text-center text-muted-foreground py-8 text-sm">Aún no hay prospectos.</p>
        ) : (
          <div className="space-y-3">
            {leads.map((l) => (
              <div key={l.lead_id} data-testid={`lead-row-${l.lead_id}`} className="rounded-2xl border border-stone-100 bg-stone-50/60 p-4 flex flex-col sm:flex-row sm:items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-outfit font-semibold text-brand-ink truncate">{l.name}</p>
                    <span className="text-xs text-muted-foreground">{l.created_at ? new Date(l.created_at).toLocaleString("es-MX") : ""}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm">
                    <a href={`mailto:${l.email}`} className="text-brand-terracotta inline-flex items-center gap-1"><Mail size={13} /> {l.email}</a>
                    {l.phone && <span className="text-muted-foreground">{l.phone}</span>}
                  </div>
                  {l.message && <p className="text-sm text-brand-ink/80 mt-2 whitespace-pre-line">{l.message}</p>}
                </div>
                <Button size="sm" variant="ghost" data-testid={`lead-delete-${l.lead_id}`} onClick={() => deleteLead(l.lead_id)} className="text-destructive hover:text-destructive gap-1.5">
                  <Trash2 size={15} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-20 flex justify-center">
          <div className="w-10 h-10 rounded-full border-4 border-brand-sage/30 border-t-brand-terracotta animate-spin" />
        </div>
      ) : (
        <div className="space-y-4" data-testid="subscribers-section">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <Users className="text-brand-terracotta" size={20} />
              <h2 className="font-outfit font-semibold text-brand-ink">Suscriptores</h2>
              <Badge className="bg-brand-ink text-white ml-1" data-testid="subscribers-count">{docs.length}</Badge>
              <Badge className="bg-emerald-600 text-white" data-testid="subscribers-active-count">{activeCount} activos</Badge>
            </div>
            <div className="relative sm:ml-auto sm:w-72">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="admin-search" value={docSearch} onChange={(e) => setDocSearch(e.target.value)}
                placeholder="Buscar por nombre o correo…"
                className="pl-9 rounded-xl bg-stone-50 border-transparent focus:bg-white" />
            </div>
          </div>

          <div className="grid gap-4">
            {filteredDocs.map((d) => (
              <div key={d.user_id} data-testid={`admin-doctor-row-${d.user_id}`}
                className="bg-white rounded-2xl border border-stone-100 p-5 shadow-sm hover:shadow-md transition-shadow">
                {/* Encabezado del médico */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-terracotta to-brand-sage text-white grid place-items-center font-outfit font-extrabold text-lg shrink-0 shadow-sm">
                    {(d.name || "?").trim().charAt(0).toUpperCase() || "?"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-outfit font-bold text-brand-ink text-base" data-testid={`admin-doctor-name-${d.user_id}`}>{d.name || "Sin nombre"}</p>
                      {statusBadge(d)}
                      {(d.role === "admin" || d.is_premium) && <Badge className="bg-brand-terracotta text-white gap-1"><Gem size={11} /> Pro</Badge>}
                      {d.has_odontogram && <Badge className="bg-brand-sage text-white gap-1"><ToothIcon size={11} /> ClinDent</Badge>}
                      {d.verification_status === "verified" && <Badge className="bg-teal-600 text-white gap-1" data-testid={`verif-badge-${d.user_id}`}><BadgeCheck size={11} /> Verificado</Badge>}
                      {d.verification_status === "pending" && <Badge className="bg-amber-500 text-white gap-1" data-testid={`verif-badge-${d.user_id}`}><Clock size={11} /> Por verificar</Badge>}
                      {d.verification_status === "rejected" && <Badge variant="destructive" data-testid={`verif-badge-${d.user_id}`}>Verif. rechazada</Badge>}
                      {d.verification_status === "correction" && <Badge className="bg-amber-600 text-white" data-testid={`verif-badge-${d.user_id}`}>Corrección</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground break-all mt-0.5">{d.email}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Acceso hasta: <span className="font-semibold text-brand-ink">{d.role === "admin" ? "Sin límite" : (d.access_until || "—")}</span>
                    </p>
                  </div>
                </div>

                {/* Acciones */}
                {d.role !== "admin" && (
                  <div className="mt-4 pt-4 border-t border-stone-100 flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="outline" data-testid={`admin-extend30-${d.user_id}`}
                      onClick={() => update(d.user_id, { extend_days: 30 }, "+30 días")}
                      className="gap-1.5">
                      <CalendarPlus size={15} /> +30 días
                    </Button>
                    <div className="flex items-center gap-1.5">
                      <Input type="date" data-testid={`admin-date-${d.user_id}`}
                        value={dates[d.user_id] ?? (d.access_until || "")}
                        onChange={(e) => setDates((s) => ({ ...s, [d.user_id]: e.target.value }))}
                        className="h-9 w-[150px]" />
                      <Button size="sm" variant="outline" data-testid={`admin-savedate-${d.user_id}`}
                        onClick={() => update(d.user_id, { access_until: dates[d.user_id] ?? d.access_until, is_active: true }, "Fecha guardada")}
                        className="gap-1.5">
                        <Save size={15} />
                      </Button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
                      <Button size="sm" variant={d.is_active === false ? "default" : "ghost"}
                        data-testid={`admin-toggle-${d.user_id}`}
                        onClick={() => update(d.user_id, { is_active: !(d.is_active !== false) }, d.is_active !== false ? "Bloqueado" : "Activado")}
                        className="gap-1.5">
                        <Power size={15} /> {d.is_active !== false ? "Bloquear" : "Activar"}
                      </Button>
                      <Button size="sm" variant={d.is_premium ? "default" : "outline"}
                        data-testid={`admin-premium-${d.user_id}`}
                        onClick={() => update(d.user_id, { is_premium: !d.is_premium }, d.is_premium ? "Plan Pro desactivado" : "Plan Pro activado")}
                        className={`gap-1.5 ${d.is_premium ? "bg-brand-terracotta hover:bg-brand-terracottaDark text-white" : ""}`}>
                        <Gem size={15} /> {d.is_premium ? "Quitar Pro" : "Dar Pro"}
                      </Button>
                      <Button size="sm" variant={d.has_odontogram ? "default" : "outline"}
                        data-testid={`admin-odontogram-${d.user_id}`}
                        onClick={() => update(d.user_id, { has_odontogram: !d.has_odontogram }, d.has_odontogram ? "ClinDent desactivado" : "ClinDent activado")}
                        className={`gap-1.5 ${d.has_odontogram ? "bg-brand-sage hover:bg-brand-sageDark text-white" : ""}`}>
                        <ToothIcon size={15} /> {d.has_odontogram ? "Quitar ClinDent" : "Dar ClinDent"}
                      </Button>
                      <Button size="sm" variant={d.has_team ? "default" : "outline"}
                        data-testid={`admin-team-${d.user_id}`}
                        onClick={() => update(d.user_id, { has_team: !d.has_team }, d.has_team ? "Equipo desactivado" : "Equipo activado (regalo)")}
                        className={`gap-1.5 ${d.has_team ? "bg-brand-ink hover:bg-brand-ink/90 text-white" : ""}`}>
                        <Users size={15} /> {d.has_team ? "Quitar Equipo" : "Regalar Equipo"}
                      </Button>
                      <Button size="sm" variant={d.has_clinpulse ? "default" : "outline"}
                        data-testid={`admin-clinpulse-${d.user_id}`}
                        onClick={() => update(d.user_id, { has_clinpulse: !d.has_clinpulse }, d.has_clinpulse ? "ClinPulse desactivado" : "ClinPulse activado (regalo)")}
                        className={`gap-1.5 ${d.has_clinpulse ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}`}>
                        <HeartPulse size={15} /> {d.has_clinpulse ? "Quitar ClinPulse" : "Regalar ClinPulse"}
                      </Button>
                      <Button size="sm" variant={d.has_clinbreath ? "default" : "outline"}
                        data-testid={`admin-clinbreath-${d.user_id}`}
                        onClick={() => update(d.user_id, { has_clinbreath: !d.has_clinbreath }, d.has_clinbreath ? "ClinBreath desactivado" : "ClinBreath activado (regalo)")}
                        className={`gap-1.5 ${d.has_clinbreath ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}`}>
                        <Wind size={15} /> {d.has_clinbreath ? "Quitar ClinBreath" : "Regalar ClinBreath"}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Verificación profesional */}
                {d.role !== "admin" && d.verification_status && d.verification_status !== "none" && (
                  <div className="mt-3 pt-3 border-t border-dashed border-stone-200" data-testid={`verif-block-${d.user_id}`}>
                    <p className="text-xs text-brand-ink/70 mb-2">
                      <ShieldCheck size={13} className="inline mr-1 text-teal-600" />
                      Cédula declarada: <b>{d.verification_cedula || d.cedula || "—"}</b>
                      {d.verification_profession ? <> · {d.verification_profession}</> : null}
                      {d.verification_specialty_cedula ? <> · Ced. esp: <b>{d.verification_specialty_cedula}</b></> : null}
                      {d.name ? <> · Nombre: <b>{d.name}</b></> : null}
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <a href={`https://www.cedulaprofesional.sep.gob.mx/cedula/presidencia/indexAvanzada.action`} target="_blank" rel="noreferrer"
                        data-testid={`verif-sep-${d.user_id}`}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 px-3 py-1.5 rounded-lg transition-colors">
                        <ExternalLink size={13} /> Abrir consulta oficial SEP
                      </a>
                      <Button size="sm" data-testid={`verif-approve-${d.user_id}`} onClick={() => decideVerif(d.user_id, "verified")}
                        className="gap-1.5 bg-teal-600 hover:bg-teal-700 text-white"><BadgeCheck size={15} /> Verificar</Button>
                      <Button size="sm" variant="outline" data-testid={`verif-correction-${d.user_id}`} onClick={() => decideVerif(d.user_id, "correction")}
                        className="gap-1.5">Solicitar corrección</Button>
                      <Button size="sm" variant="outline" data-testid={`verif-reject-${d.user_id}`} onClick={() => decideVerif(d.user_id, "rejected")}
                        className="gap-1.5 text-red-600 hover:bg-red-50">Rechazar</Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {docs.length === 0 && (
              <p className="text-center text-muted-foreground py-16">No hay médicos registrados.</p>
            )}
            {docs.length > 0 && filteredDocs.length === 0 && (
              <p className="text-center text-muted-foreground py-10" data-testid="admin-search-empty">Sin resultados para “{docSearch}”.</p>
            )}
          </div>
        </div>
      )}

      {/* Bitácora de accesos (NOM-024) */}
      {!loading && (
        <div className="mt-10">
          <div className="flex items-center gap-2 mb-4">
            <Database size={18} className="text-brand-sage" />
            <h2 className="font-outfit text-xl font-bold text-brand-ink">Bitácora de accesos (NOM-024)</h2>
          </div>
          <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden" data-testid="access-log">
            {accessLog.length === 0 ? (
              <p className="text-center text-muted-foreground py-10 text-sm">Sin registros de acceso todavía.</p>
            ) : (
              <div className="max-h-96 overflow-y-auto divide-y divide-stone-50">
                {accessLog.map((l) => (
                  <div key={l.log_id} data-testid={`access-log-row`} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                    <div className="min-w-0">
                      <span className="font-semibold text-brand-ink capitalize">{l.action}</span>
                      <span className="text-muted-foreground"> · {l.entity}{l.detail ? ` · ${l.detail}` : ""}</span>
                      <p className="text-xs text-muted-foreground truncate">{l.doctor_name}</p>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{(() => { try { return new Date(l.at).toLocaleString("es-MX"); } catch { return l.at; } })()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {receiptView && (
        <div data-testid="receipt-lightbox" onClick={() => setReceiptView(null)}
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-6 cursor-zoom-out">
          <button onClick={() => setReceiptView(null)} className="absolute top-5 right-5 text-white text-2xl font-bold w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20" data-testid="receipt-lightbox-close">✕</button>
          <img src={receiptView} alt="Comprobante" className="max-h-[88vh] max-w-full rounded-xl shadow-2xl object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
